import { Vector2 } from "../../common/vector2.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import type { GameStatistics } from "../../storage/statistics_storage.js";
import { Easing } from "../../transforms/transform_base.js";
import { TransformOpacity } from "../../transforms/transform_opacity.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { TransformValue } from "../../transforms/transform_value.js";
import { heartCountText, heartTimerText } from "../../utils/heart_timer.js";
import { DialogManager } from "../dialog/dialog_manager.js";
import { MoreLivesDialog } from "../dialog/more_lives_dialog.js";
import { OverlayFactory } from "../overlay/overlay_factory.js";
import { OverlayId } from "../overlay/overlay_ids.js";
import { ColorScheme } from "./color_scheme.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { UIElement } from "./ui_element.js";
import { UIElementHolder } from "./ui_element_holder.js";
import { UIImage } from "./ui_image.js";
import { UITextElement } from "./ui_text_element.js";

export type CurrencyBarKind = "heart" | "coin";

export interface CurrencyBarOptions
{
  clickable?: boolean;
  show_plus?: boolean;
  stats?: GameStatistics;
}

interface CurrencyBlock
{
  icon: UIImage;
  count: UITextElement;
  icon_count: UITextElement | null;
  plus: UIImage | null;
}

export class CurrencyBar extends UIElementHolder
{
  private static readonly COIN_X_MIN = 285;
  private static readonly COIN_X_MAX = 430;
  private static readonly HEART_X_MIN = 620;
  private static readonly HEART_X_MAX = 835;
  public static readonly BLOCK_Y = 123;
  public static readonly BLOCK_W = 310;
  public static readonly BLOCK_H = 100;
  private static readonly HEART_ICON_SIZE = 174;
  public static readonly COIN_ICON_SIZE = 156;
  public static readonly ICON_X = -35;
  public static readonly COUNT_W = 190;
  public static readonly COUNT_RIGHT_PAD = 15;
  public static readonly COUNT_FONT = 45;
  private static readonly HEART_COUNT_FONT = 70;
  private static readonly HEART_COUNT_Y = -7;
  private static readonly HEART_COUNT_SHADOW_BLUR = 6;
  private static readonly HEART_COUNT_SHADOW_Y = 3;
  private static readonly PLUS_SIZE = 65;
  private static readonly PLUS_INSET = 10;

  private static readonly HEART_APPEAR_FROM = 0.7;
  private static readonly HEART_APPEAR_OVERSHOOT = 1.1;
  private static readonly HEART_SHRINK_MS = 150;
  private static readonly HEART_APPEAR_UP_MS = 204;
  private static readonly HEART_APPEAR_SETTLE_MS = 144;
  private static readonly HEART_DIGIT_DIM = 0.3;

  private readonly heart: CurrencyBlock;
  private readonly coin: CurrencyBlock;

  private shown_coins: number;
  private shown_hearts: number;
  private hearts_displayed: number;
  private shown_hearts_infinite: boolean;
  private shown_timer: string;
  private hearts_pop_active = false;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, options: CurrencyBarOptions = {} )
  {
    const heart_x = LayoutScaler.xFromLayouts( CurrencyBar.HEART_X_MIN, CurrencyBar.HEART_X_MAX );
    const coin_x = LayoutScaler.xFromLayouts( CurrencyBar.COIN_X_MIN, CurrencyBar.COIN_X_MAX );
    const icon_x = LayoutScaler.xFromDesign( CurrencyBar.ICON_X );
    const bg_w = LayoutScaler.widthFromDesign( CurrencyBar.BLOCK_W );
    const bg_h = LayoutScaler.heightFromDesign( CurrencyBar.BLOCK_H );
    const heart_icon_h = LayoutScaler.heightFromDesign( CurrencyBar.HEART_ICON_SIZE );
    const heart_icon_y = bg_h / 2 - heart_icon_h / 2;
    const left_x = Math.min( heart_x, coin_x );
    const heart_block_x = heart_x - left_x - icon_x;
    const coin_block_x = coin_x - left_x - icon_x;
    const block_y = -heart_icon_y;
    const bar_w = Math.max( heart_block_x, coin_block_x ) + bg_w;
    const bar_h = heart_icon_h;

    super( ctx, x, y, bar_w, bar_h, false );

    const stats = options.stats ?? StatisticsStorage.load();
    const now_ms = Date.now();
    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    const timer = heartTimerText( stats, now_ms );
    const clickable = options.clickable ?? false;
    const show_plus = options.show_plus ?? false;

    this.shown_coins = stats.coins;
    this.shown_hearts = stats.hearts;
    this.hearts_displayed = stats.hearts;
    this.shown_hearts_infinite = hearts_infinite;
    this.shown_timer = timer;

    this.heart = this.createBlock(
      heart_block_x,
      block_y,
      "heart",
      hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart,
      timer,
      hearts_infinite ? "" : heartCountText( stats, now_ms ),
      clickable,
      show_plus
    );

    this.coin = this.createBlock(
      coin_block_x,
      block_y,
      "coin",
      ImgID.IconCoin,
      String( stats.coins ),
      null,
      clickable,
      show_plus
    );

    this.setHeartPlusVisible( !hearts_infinite && timer !== "MAX" );
    this.setClickable( clickable );
  }

  public static defaultPosition(): Vector2
  {
    const heart_x = LayoutScaler.xFromLayouts( CurrencyBar.HEART_X_MIN, CurrencyBar.HEART_X_MAX );
    const coin_x = LayoutScaler.xFromLayouts( CurrencyBar.COIN_X_MIN, CurrencyBar.COIN_X_MAX );
    const left_x = Math.min( heart_x, coin_x );
    const icon_x = LayoutScaler.xFromDesign( CurrencyBar.ICON_X );
    const block_y = LayoutScaler.yFromDesign( CurrencyBar.BLOCK_Y );
    const bg_h = LayoutScaler.heightFromDesign( CurrencyBar.BLOCK_H );
    const heart_icon_h = LayoutScaler.heightFromDesign( CurrencyBar.HEART_ICON_SIZE );
    const heart_icon_y = bg_h / 2 - heart_icon_h / 2;

    return new Vector2( left_x + icon_x, block_y + heart_icon_y );
  }

  public static defaultCoinBlockX(): number
  {
    return LayoutScaler.xFromLayouts( CurrencyBar.COIN_X_MIN, CurrencyBar.COIN_X_MAX );
  }

  public static defaultCoinCenter(): Vector2
  {
    const coin_x = CurrencyBar.defaultCoinBlockX();
    const icon_x = LayoutScaler.xFromDesign( CurrencyBar.ICON_X );
    const block_y = LayoutScaler.yFromDesign( CurrencyBar.BLOCK_Y );
    const bg_h = LayoutScaler.heightFromDesign( CurrencyBar.BLOCK_H );
    const coin_icon_w = LayoutScaler.heightFromDesign( CurrencyBar.COIN_ICON_SIZE );

    return new Vector2( coin_x + icon_x + coin_icon_w / 2, block_y + bg_h / 2 );
  }

  public heartIcon(): UIImage
  {
    return this.heart.icon;
  }

  public coinIcon(): UIImage
  {
    return this.coin.icon;
  }

  public heartTarget(): Vector2
  {
    return CurrencyBar.absoluteCenter( this.heart.icon );
  }

  public coinTarget(): Vector2
  {
    return CurrencyBar.absoluteCenter( this.coin.icon );
  }

  public sync(): void
  {
    const stats = StatisticsStorage.load();
    const now_ms = Date.now();

    if ( stats.coins !== this.shown_coins )
    {
      this.shown_coins = stats.coins;
      this.coin.count.setText( String( stats.coins ) );
    }

    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    if ( hearts_infinite !== this.shown_hearts_infinite )
    {
      this.shown_hearts_infinite = hearts_infinite;
      this.shown_hearts = stats.hearts;
      this.hearts_displayed = stats.hearts;
      this.heart.icon.setImageId( hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart );
      this.heart.icon_count?.setText( hearts_infinite ? "" : heartCountText( stats, now_ms ) );
      this.forceMakeShouldBeRendered();
    }
    else if ( hearts_infinite )
    {
      this.shown_hearts = stats.hearts;
      this.hearts_displayed = stats.hearts;
    }
    else if ( stats.hearts !== this.shown_hearts )
    {
      this.shown_hearts = stats.hearts;
      this.startHeartsPop();
      this.forceMakeShouldBeRendered();
    }

    const timer = heartTimerText( stats, now_ms );
    if ( timer !== this.shown_timer )
    {
      this.shown_timer = timer;
      this.heart.count.setText( timer );
    }

    this.setHeartPlusVisible( !hearts_infinite && timer !== "MAX" );
  }

  public animateCoinsTo( to: number, start_time: number, duration_ms: number ): void
  {
    const from = this.shown_coins;
    this.shown_coins = to;

    if ( from === to )
    {
      this.coin.count.setText( String( to ) );
      return;
    }

    this.addTransform( new TransformValue(
      from,
      to,
      start_time,
      duration_ms,
      value => this.coin.count.setText( String( Math.round( value ) ) ),
      { easing: Easing.EaseOutQuad }
    ) );
  }

  public refreshHearts( stats: GameStatistics = StatisticsStorage.load(), now_ms = Date.now() ): void
  {
    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    const timer = heartTimerText( stats, now_ms );

    this.shown_hearts_infinite = hearts_infinite;
    this.shown_hearts = stats.hearts;
    this.hearts_displayed = stats.hearts;
    this.shown_timer = timer;

    this.heart.count.setText( timer );
    this.heart.icon_count?.setText( hearts_infinite ? "" : heartCountText( stats, now_ms ) );
    this.heart.icon.setImageId( hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart );
    this.setHeartPlusVisible( !hearts_infinite && timer !== "MAX" );
    this.forceMakeShouldBeRendered();
  }

  private createBlock(
    x: number,
    y: number,
    kind: CurrencyBarKind,
    img_id: ImgID,
    count: string,
    icon_count: string | null,
    clickable: boolean,
    show_plus: boolean
  ): CurrencyBlock
  {
    const bg_w = LayoutScaler.widthFromDesign( CurrencyBar.BLOCK_W );
    const bg_h = LayoutScaler.heightFromDesign( CurrencyBar.BLOCK_H );
    const icon_size = kind === "coin" ? CurrencyBar.COIN_ICON_SIZE : CurrencyBar.HEART_ICON_SIZE;
    const icon_w = LayoutScaler.heightFromDesign( icon_size );
    const icon_h = icon_w;
    const icon_x = LayoutScaler.xFromDesign( CurrencyBar.ICON_X );
    const icon_y = bg_h / 2 - icon_h / 2;

    const holder_left = Math.min( 0, icon_x );
    const holder_top = Math.min( 0, icon_y );
    const holder_right = Math.max( bg_w, icon_x + icon_w );
    const holder_bottom = Math.max( bg_h, icon_y + icon_h );
    const holder_x = x + holder_left;
    const holder_y = y + holder_top;
    const holder_w = holder_right - holder_left;
    const holder_h = holder_bottom - holder_top;

    const holder = new UIElementHolder( this.ctx, holder_x, holder_y, holder_w, holder_h, false, clickable );
    this.addChild( holder );

    if ( clickable )
      holder.setOnClick( () => CurrencyBar.openDialog( kind ) );
    else
      holder.setClickable( false );

    const bg_x = -holder_left;
    const bg_y = -holder_top;

    const bg = new UIImage( this.ctx, bg_x, bg_y, bg_w, bg_h, ImgID.UI_MenuBlockBg );
    bg.setClickable( false );
    holder.addChild( bg );

    const count_w = LayoutScaler.widthFromDesign( CurrencyBar.COUNT_W );
    const count_right_pad = LayoutScaler.widthFromDesign( CurrencyBar.COUNT_RIGHT_PAD );
    const count_x = bg_w - count_w - count_right_pad;
    const count_font = LayoutScaler.textSizeFromDesign( CurrencyBar.COUNT_FONT );

    const count_text = new UITextElement(
      this.ctx,
      count_x,
      0,
      count_w,
      bg_h,
      {
        text: count,
        font_size: count_font,
        font_weight: 700,
        text_color: ColorScheme.menu_top_el_text,
        text_style: TextStyleScheme.rubik,
      }
    );
    count_text.setClickable( false );
    bg.addChild( count_text );

    const icon = new UIImage( this.ctx, icon_x, icon_y, icon_w, icon_h, img_id, {}, false );
    icon.setClickable( false );
    bg.addChild( icon );

    let icon_count_text: UITextElement | null = null;
    if ( icon_count !== null )
    {
      const icon_count_y = LayoutScaler.yFromDesign( CurrencyBar.HEART_COUNT_Y );
      const icon_count_font = LayoutScaler.textSizeFromDesign( CurrencyBar.HEART_COUNT_FONT );
      const icon_count_blur = LayoutScaler.widthFromDesign( CurrencyBar.HEART_COUNT_SHADOW_BLUR );
      const icon_count_shadow_y = LayoutScaler.yFromDesign( CurrencyBar.HEART_COUNT_SHADOW_Y );

      icon_count_text = new UITextElement(
        this.ctx,
        0,
        icon_count_y,
        icon_w,
        icon_h,
        {
          text: icon_count,
          font_size: icon_count_font,
          font_weight: 700,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.rubik,
          shadow_blur: icon_count_blur,
          shadow_color: ColorScheme.black_transparent,
          shadow_offset_y: icon_count_shadow_y,
        }
      );
      icon_count_text.setClickable( false );
      icon.addChild( icon_count_text );
    }

    let plus: UIImage | null = null;
    if ( show_plus )
    {
      const plus_w = LayoutScaler.widthFromDesign( CurrencyBar.PLUS_SIZE );
      const plus_h = LayoutScaler.heightFromDesign( CurrencyBar.PLUS_SIZE );
      const plus_x = icon_w - plus_w - LayoutScaler.xFromDesign( CurrencyBar.PLUS_INSET );
      const plus_y = icon_h - plus_h - LayoutScaler.yFromDesign( CurrencyBar.PLUS_INSET );

      plus = new UIImage( this.ctx, plus_x, plus_y, plus_w, plus_h, ImgID.IconPlus, {}, false );
      plus.setClickable( false );
      icon.addChild( plus );
    }

    return { icon, count: count_text, icon_count: icon_count_text, plus };
  }

  private static openDialog( kind: CurrencyBarKind ): void
  {
    if ( kind === "coin" )
    {
      OverlayFactory.openOverlay( OverlayId.SHOP_OVERLAY );
      return;
    }

    void audioController.playSound( AudioClips.UI_CLICK );
    DialogManager.openDialog( new MoreLivesDialog() );
  }

  private static absoluteCenter( el: UIElement ): Vector2
  {
    const pos = el.getAbsolutePosition();
    const rect = el.getRect();
    return new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 );
  }

  private setHeartPlusVisible( visible: boolean ): void
  {
    const plus = this.heart.plus;
    if ( !plus )
      return;

    const opacity = visible ? 1 : 0;
    if ( plus.getOpacityImportant() === opacity )
      return;

    plus.setOpacityImmediate( opacity );
    plus.forceMakeShouldBeRendered();
  }

  private startHeartsPop(): void
  {
    if ( this.hearts_pop_active )
      return;

    this.hearts_pop_active = true;
    this.playHeartPop();
  }

  private playHeartPop(): void
  {
    const icon = this.heart.icon;
    const text = this.heart.icon_count;

    if ( !text || this.hearts_displayed === this.shown_hearts )
    {
      this.hearts_pop_active = false;
      return;
    }

    const next = this.hearts_displayed + ( this.shown_hearts > this.hearts_displayed ? 1 : -1 );
    const now = performance.now();

    const from = new Vector2( CurrencyBar.HEART_APPEAR_FROM, CurrencyBar.HEART_APPEAR_FROM );
    const overshoot = new Vector2( CurrencyBar.HEART_APPEAR_OVERSHOOT, CurrencyBar.HEART_APPEAR_OVERSHOOT );
    const normal = new Vector2( 1, 1 );

    const up_start = now + CurrencyBar.HEART_SHRINK_MS;
    const settle_start = up_start + CurrencyBar.HEART_APPEAR_UP_MS;

    const shrink = new TransformScale(
      icon, true, normal, from,
      now, CurrencyBar.HEART_SHRINK_MS,
      { easing: Easing.EaseInOutQuad }
    );
    shrink.setOnFinished( () =>
    {
      this.hearts_displayed = next;
      text.setText( String( next ) );
    } );
    icon.addTransform( shrink );

    icon.addTransform( new TransformScale(
      icon, true, from, overshoot,
      up_start, CurrencyBar.HEART_APPEAR_UP_MS,
      { easing: Easing.EaseOutQuad }
    ) );

    const settle = new TransformScale(
      icon, true, overshoot, normal,
      settle_start, CurrencyBar.HEART_APPEAR_SETTLE_MS,
      { easing: Easing.EaseOutQuad }
    );
    settle.setOnFinished( () => this.playHeartPop() );
    icon.addTransform( settle );

    text.addTransform( new TransformOpacity(
      text, true, 1, CurrencyBar.HEART_DIGIT_DIM,
      now, CurrencyBar.HEART_SHRINK_MS,
      { easing: Easing.EaseInOutQuad }
    ) );
    text.addTransform( new TransformOpacity(
      text, true, CurrencyBar.HEART_DIGIT_DIM, 1,
      up_start, CurrencyBar.HEART_APPEAR_UP_MS,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  public name(): string
  {
    return "CurrencyBar";
  }
}
